import { TabBar } from '@components/TabBar';
import { TabControl } from '@components/TabControl';
import { TabPage } from '@components/TagPage';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { useTab } from '@hooks/useTab';
import { useValidation } from '@hooks/useValidation';
import { makeStyles, Tab } from '@material-ui/core';
import { useEmpresaCombo } from '@model/Empresa';
import IFatura from '@model/Fatura';
import ILinha from '@model/Linha';
import { IVendedor } from '@model/Vendedor';
import { IVendedorMovimentacao } from '@model/VendedorMovimentacao';
import { IVendedorComissaoFilter, IVendedorComissaoRecord } from '@services/VendedorComissaoService';
import { IVendedorMovimentacaoFilter } from '@services/VendedorMovimentacaoService';
import { useVendedorService } from '@services/VendedorService';
import { currentCompetence } from '@utils/Functions';
import { yup } from '@validations/Yup';
import React from 'react';
import { FichaVendedorComissao } from './Comissoes';
import { FichaVendedorInadimplentes } from './Inadimplentes';
import { FichaVendedorInventario } from './Inventario';
import { FichaVendedorMovimentacao } from './Movimentacoes';

enum EnumPages {
    Inventario = 0,
    Comissoes = 1,
    Movimentacoes = 2,
    Inadimplentes = 3
}

export type FichaVendedorTabProps = {
    vendedor: IVendedor
}

export function FichaVendedorTab ({ vendedor }: FichaVendedorTabProps) {
    const cls = useStyles();

    const tab = useTab();

    const inventarioLoading = useLoading();
    const comissaoLoading = useLoading();
    const movimentacaoLoading = useLoading();
    const inadimplentesLoading = useLoading();
    const comboEmpresa = useEmpresaCombo()

    const message = useMessage();

    const vendedorService = useVendedorService();

    const inventarioData = useData<ILinha, {}>({
    })

    const movimentacaoData = useData<IVendedorMovimentacao, IVendedorMovimentacaoFilter>({
        filter: {
            ano: useField(new Date())
        }
    })

    const comissaoData = useData<IVendedorComissaoRecord, IVendedorComissaoFilter>({
        filter: {
            competencia: useField(currentCompetence())
        }
    })

    const inadimplentesData = useData<IFatura>();

    const movimentacaoValidation = useValidation<IVendedorMovimentacaoFilter>({
        ano: yup.date().required()
    });

    const listarInventarios = React.useCallback(() => {
        if (!inventarioLoading.isLoading) {
            inventarioLoading.start();
            vendedorService.listarInventario(vendedor.vendedorId)
                .then(inventarioData.handleDataResponse)
                .catch(message.showException)
                .finally(inventarioLoading.stop);
        }
    }, [inventarioData.handleDataResponse, inventarioLoading, message.showException, vendedor.vendedorId, vendedorService]);

    const listarMovimentacoes = React.useCallback(() => {
        if (!movimentacaoLoading.isLoading) {
            const filterValues = movimentacaoData.filter.getInputValues();

            movimentacaoData.filter.clearErrors();

            movimentacaoValidation.validate(filterValues)
                .then(() => {
                    movimentacaoLoading.start();
                    vendedorService.listarMovimentacoes(vendedor.vendedorId, filterValues)
                        .then(movimentacaoData.handleDataResponse)
                        .catch(message.showException)
                        .finally(movimentacaoLoading.stop);
                })
                .catch(movimentacaoData.filter.setErrorsFromYup);
        }
    }, [message.showException, movimentacaoData.filter, movimentacaoData.handleDataResponse, movimentacaoLoading, movimentacaoValidation, vendedor.vendedorId, vendedorService]);

    const listarComissoes = React.useCallback(() => {
        if (!comissaoLoading.isLoading) {
            const filterValues = comissaoData.filter.getInputValues();

            comissaoLoading.start();
            vendedorService.listarComissoes(vendedor.vendedorId, filterValues)
                .then(comissaoData.handleDataResponse)
                .catch(message.showException)
                .finally(comissaoLoading.stop);
        }
    }, [comissaoData.filter, comissaoData.handleDataResponse, comissaoLoading, message.showException, vendedor.vendedorId, vendedorService]);

    const listarInadimplentes = React.useCallback(() => {
        if (!inadimplentesLoading.isLoading) {
            inadimplentesLoading.start();
            vendedorService.listarInadimplentes(vendedor.vendedorId)
                .then(inadimplentesData.handleDataResponse)
                .catch(message.showException)
                .finally(inadimplentesLoading.stop);
}
    }, [inadimplentesData.handleDataResponse, inadimplentesLoading, message.showException, vendedor.vendedorId, vendedorService]);

    React.useEffect(() => {
        listarInventarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inventarioData.filter.requestedFilter]);

    React.useEffect(() => {
        listarMovimentacoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [movimentacaoData.filter.requestedFilter]);

    React.useEffect(() => {
        listarComissoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comissaoData.filter.requestedFilter]);

    useEffectOnce(() => {
        listarInadimplentes();
        comboEmpresa.loadOptions();
    });

    return (
        <React.Fragment>
            <TabBar className={cls.root}>
                <TabControl tab={tab}>
                    <Tab label="Inventário" value={EnumPages.Inventario}></Tab>
                    <Tab label="Movimentações" value={EnumPages.Movimentacoes}></Tab>
                    <Tab label="Comissões" value={EnumPages.Comissoes}></Tab>
                    <Tab label="Inadimplentes" value={EnumPages.Inadimplentes}></Tab>
                </TabControl>
            </TabBar>
        
            <TabPage index={EnumPages.Inventario} tabIndex={tab.tabIndex}>
                <FichaVendedorInventario
                    vendedor={vendedor}
                    data={inventarioData}
                    dataMovimentacao={movimentacaoData}
                    isLoading={inventarioLoading.isLoading}
                />            
            </TabPage>

            <TabPage index={EnumPages.Movimentacoes} tabIndex={tab.tabIndex}>
                <FichaVendedorMovimentacao
                    vendedor={vendedor}
                    data={movimentacaoData}
                    dataInventario={inventarioData}
                    isLoading={movimentacaoLoading.isLoading}
                />            
            </TabPage>

            <TabPage index={EnumPages.Comissoes} tabIndex={tab.tabIndex}>
                <FichaVendedorComissao
                    data={comissaoData}
                    isLoading={comissaoLoading.isLoading}
                    vendedor={vendedor}
                    comboEmpresa={comboEmpresa}
                />            
            </TabPage>

            <TabPage index={EnumPages.Inadimplentes} tabIndex={tab.tabIndex}>
                <FichaVendedorInadimplentes
                    data={inadimplentesData}
                    isLoading={inadimplentesLoading.isLoading}
                />            
            </TabPage>
        </React.Fragment>
    )
}

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: theme.spacing(2)
    }
}))